import { PanelLeft } from "lucide-react";
import { useMemo, type FC, type ReactElement, type ReactNode } from "react";

import { HeaderIconButton } from "@/components/app-header";
import {
  useRegisterActiveTab,
  type ActiveTabRegistration
} from "@/features/appTabs/context/ActiveTabContext";
import { clientSubtitle } from "@/features/appTabs/groups";
import { clientTabId } from "@/features/appTabs/tabRoutes";

import {
  ClientProfileBridge,
  useClientProfile
} from "../context/ClientProfileContext";
import { ClientDetailsPopover } from "./ClientDetailsPopover";
import { useClientDetails } from "./useClientDetails";

/** The client nav toggle; the nav is also the Requests explorer. */
export const ClientRailToggle: FC = () => {
  const { nav } = useClientProfile();
  return (
    <HeaderIconButton
      label={nav.isOpen ? "Hide Client Navigation" : "Show Client Navigation"}
      pressed={nav.isOpen}
      onClick={nav.toggle}
    >
      <PanelLeft className="size-4" />
    </HeaderIconButton>
  );
};

/**
 * The client's part of the app tab bar: the tab's name and photo, the rail
 * toggle, and the details popover the active client tab opens. Renders
 * nothing itself.
 */
export const ClientHeader: FC = () => {
  const profile = useClientProfile();
  const details = useClientDetails();
  const { isLoading, clientId, client, name, initials, avatarUrl } = details;
  const subtitle = clientSubtitle(client);

  const meta = useMemo(
    () =>
      isLoading || !name
        ? undefined
        : { title: name, initials, avatarUrl, subtitle },
    [isLoading, name, initials, avatarUrl, subtitle]
  );

  const reg = useMemo<ActiveTabRegistration | null>(() => {
    if (!clientId) return null;
    const bridged = (node: ReactNode) => (
      <ClientProfileBridge value={profile}>{node}</ClientProfileBridge>
    );
    return {
      tabId: clientTabId(clientId),
      meta,
      left: bridged(<ClientRailToggle />),
      renderDetails: isLoading
        ? undefined
        : (trigger: ReactElement) =>
            bridged(
              <ClientDetailsPopover details={details}>
                {trigger}
              </ClientDetailsPopover>
            ),
      fallbackBackTo: "/clients"
    };
  }, [clientId, details, isLoading, meta, profile]);
  useRegisterActiveTab(reg);
  return null;
};
